import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { api, useAction, useApi } from "../../api.ts";
import { useMe } from "../../auth.tsx";
import { fmtDate, fmtDateTime, fmtExpiry, fmtMoney, fmtRelative, shortHash } from "../../format.ts";
import {
  Badge, Button, Card, CopyButton, Dialog, Drawer, EmptyState, ErrorText, Field, Input, Loading, LoadError, Mono, PageHeader, Pagination,
  SearchInput, Select, Switch, Table, TBody, Td, Th, THead, Tabs, Tr, useConfirm, useDebounced, useToast,
} from "../../ui/index.tsx";
import {
  DEFAULT_DURATION, DurationField, GamePicker, SecretBox, TICKET_STATUS, durationDays, fmtDuration, useGames, type DurationValue,
} from "./_common.tsx";
import type { AdminUserDetail, AdminUserRow, Entitlement, Game, Paged, Role } from "../../../shared/types.ts";

type Filter = "all" | "active" | "disabled" | "admins" | "licensed" | "unlicensed";
const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "disabled", label: "Disabled" },
  { id: "admins", label: "Admins" },
  { id: "licensed", label: "Licensed" },
  { id: "unlicensed", label: "Unlicensed" },
];

export default function Users() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const filter = (FILTERS.find((f) => f.id === params.get("filter"))?.id ?? "all") as Filter;
  const page = Math.max(1, Number(params.get("page")) || 1);

  const [search, setSearch] = useState(q);
  const debounced = useDebounced(search, 300);
  useEffect(() => {
    if (debounced === q) return;
    const p = new URLSearchParams(params);
    debounced ? p.set("q", debounced) : p.delete("q");
    p.delete("page");
    setParams(p, { replace: true });
  }, [debounced]);

  const list = useApi<Paged<AdminUserRow>>(`/admin/users?q=${encodeURIComponent(q)}&filter=${filter}&page=${page}&limit=25`);
  const { games, nameOf, loading: gamesLoading } = useGames();
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ username: string; password: string } | null>(null);

  const setQuery = (next: { filter?: Filter; page?: number }) => {
    const p = new URLSearchParams(params);
    if (next.filter !== undefined) { next.filter === "all" ? p.delete("filter") : p.set("filter", next.filter); p.delete("page"); }
    if (next.page !== undefined) next.page > 1 ? p.set("page", String(next.page)) : p.delete("page");
    setParams(p, { replace: true });
  };

  const open = (userId: string) => navigate({ pathname: `/admin/users/${userId}`, search: location.search });
  const close = () => navigate({ pathname: "/admin/users", search: location.search });
  const data = list.data;

  return (
    <>
      <PageHeader title="Users" description="Accounts, licenses and devices"
        actions={<Button variant="primary" icon="plus" onClick={() => setCreating(true)}>New user</Button>} />

      <Card padded={false}>
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search username, IP or HWID…" className="w-full sm:w-72" />
        </div>
        <div className="mt-2 px-4">
          <Tabs tabs={FILTERS} value={filter} onChange={(f) => setQuery({ filter: f })} />
        </div>

        {list.error && !data ? <LoadError error={list.error} retry={list.reload} /> : !data ? <Loading /> : data.items.length === 0 ? (
          <EmptyState icon="users" title="No users found" body={q ? `Nothing matches "${q}".` : "Try a different filter."} />
        ) : (
          <>
            <Table>
              <THead><tr><Th>User</Th><Th>Games</Th><Th>Expires</Th><Th>Last login</Th><Th>Country</Th><Th>Status</Th></tr></THead>
              <TBody>
                {data.items.map((u) => (
                  <Tr key={u.id} onClick={() => open(u.id)}>
                    <Td>
                      <div className="flex items-center gap-2">
                        <span title={u.online ? "Online" : "Offline"} className={`size-2 shrink-0 rounded-full ${u.online ? "bg-ok" : "bg-line-strong"}`} />
                        <span className="font-medium">{u.username}</span>
                        {u.role === "admin" && <Badge tone="accent">Admin</Badge>}
                      </div>
                    </Td>
                    <Td>
                      <div className="flex max-w-60 flex-wrap gap-1">
                        {u.games.length ? u.games.map((g) => <Badge key={g}>{nameOf(g)}</Badge>) : <span className="text-faint">—</span>}
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap text-muted">{u.games.length ? (u.next_expiry ? fmtExpiry(u.next_expiry) : "Lifetime") : "—"}</Td>
                    <Td className="whitespace-nowrap text-muted" >{u.last_login ? <span title={fmtDateTime(u.last_login)}>{fmtRelative(u.last_login)}</span> : "Never"}</Td>
                    <Td className="text-muted">{u.country ?? "—"}</Td>
                    <Td>
                      <div className="flex gap-1">
                        {u.active ? <Badge tone="ok" dot>Active</Badge> : <Badge tone="bad" dot>Disabled</Badge>}
                        {!u.has_password && <Badge tone="warn">No password</Badge>}
                      </div>
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
            <Pagination page={data.page} pages={data.pages} total={data.total} onChange={(p) => setQuery({ page: p })} />
          </>
        )}
      </Card>

      {id && <UserDrawer key={id} id={id} games={games} nameOf={nameOf} onClose={close} onChanged={() => { void list.reload(); }} />}

      <CreateUserDialog open={creating} games={games} gamesLoading={gamesLoading} onClose={() => setCreating(false)}
        onCreated={(r) => { setCreating(false); setCreated(r); void list.reload(); }} />
      <Dialog open={!!created} onClose={() => setCreated(null)} title="User created"
        description="This password is shown once. Copy it now."
        footer={<Button variant="primary" onClick={() => setCreated(null)}>Done</Button>}>
        {created && (
          <div className="space-y-3">
            <Field label="Username"><SecretBox value={created.username} /></Field>
            <Field label="Password"><SecretBox value={created.password} /></Field>
          </div>
        )}
      </Dialog>
    </>
  );
}

function CreateUserDialog({ open, games, gamesLoading, onClose, onCreated }: {
  open: boolean; games: Game[]; gamesLoading: boolean; onClose: () => void; onCreated: (r: { username: string; password: string }) => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("user");
  const [picked, setPicked] = useState<string[]>([]);
  const [duration, setDuration] = useState<DurationValue>(DEFAULT_DURATION);
  const [formError, setFormError] = useState<string | null>(null);
  const create = useAction((body: object) => api.post<{ user: AdminUserRow; password: string }>("/admin/users", body));

  const submit = async () => {
    const name = username.trim();
    const days = durationDays(duration);
    if (!name) return setFormError("Username is required.");
    if (picked.length && days === undefined) return setFormError("Enter a valid number of days, or choose lifetime.");
    setFormError(null);
    const res = await create.run({
      username: name,
      password: password || undefined,
      role,
      games: picked.length ? picked : undefined,
      days: picked.length ? days : undefined,
    });
    if (res) {
      setUsername(""); setPassword(""); setRole("user"); setPicked([]); setDuration(DEFAULT_DURATION);
      onCreated({ username: res.user.username, password: res.password });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="New user"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={create.pending} onClick={submit}>Create user</Button>
      </>}>
      <div className="space-y-4">
        <Field label="Username"><Input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" /></Field>
        <Field label="Password" hint="Leave blank to generate one"><Input value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" /></Field>
        <Field label="Role">
          <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </Select>
        </Field>
        <Field label="Games" hint="Optional. Grants access on creation."><GamePicker games={games} loading={gamesLoading} value={picked} onChange={setPicked} /></Field>
        {picked.length > 0 && <DurationField value={duration} onChange={setDuration} />}
        <ErrorText>{formError ?? create.error}</ErrorText>
      </div>
    </Dialog>
  );
}

// ── Detail drawer ──

type ActivityTab = "logins" | "tickets" | "payments" | "errors" | "redemptions";

function UserDrawer({ id, games, nameOf, onClose, onChanged }: {
  id: string; games: Game[]; nameOf: (id: string) => string; onClose: () => void; onChanged: () => void;
}) {
  const detail = useApi<AdminUserDetail>(`/admin/users/${id}`);
  const me = useMe();
  const confirm = useConfirm();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const d = detail.data;
  const isSelf = id === me.id;

  const mutate = async (key: string, fn: () => Promise<AdminUserDetail | unknown>, success: string) => {
    setBusy(key);
    try {
      const res = await fn();
      if (res && typeof res === "object" && "user" in res && "entitlements" in res) detail.setData(res as AdminUserDetail);
      toast(success, "success");
      onChanged();
      return true;
    } catch (e) {
      toast(e instanceof Error ? e.message : "Action failed", "error");
      return false;
    } finally {
      setBusy(null);
    }
  };

  const patch = (key: string, body: object, success: string) => mutate(key, () => api.patch<AdminUserDetail>(`/admin/users/${id}`, body), success);

  const toggleActive = async () => {
    if (!d) return;
    const disabling = d.user.active;
    if (!(await confirm({
      title: disabling ? `Disable ${d.user.username}?` : `Enable ${d.user.username}?`,
      body: disabling ? "They will be signed out and unable to log in." : "They will be able to log in again.",
      confirmLabel: disabling ? "Disable" : "Enable", danger: disabling,
    }))) return;
    await patch("active", { active: !disabling }, disabling ? "User disabled" : "User enabled");
  };

  const toggleRole = async () => {
    if (!d) return;
    const next: Role = d.user.role === "admin" ? "user" : "admin";
    if (!(await confirm({
      title: next === "admin" ? `Make ${d.user.username} an admin?` : `Remove admin from ${d.user.username}?`,
      body: next === "admin" ? "Admins have full access to this panel." : "They will lose access to this panel.",
      confirmLabel: next === "admin" ? "Make admin" : "Make user", danger: next === "user",
    }))) return;
    await patch("role", { role: next }, `Role set to ${next}`);
  };

  const resetHwid = async () => {
    if (!d) return;
    if (!(await confirm({ title: "Reset HWID?", body: `${d.user.username} will be able to bind a new device on next login.`, confirmLabel: "Reset" }))) return;
    await mutate("hwid", () => api.post<AdminUserDetail>(`/admin/users/${id}/reset-hwid`), "HWID reset");
  };

  const kick = async () => {
    if (!d) return;
    if (!(await confirm({ title: `Kick ${d.user.username}?`, body: "Disconnects every live connection. They can sign in again.", confirmLabel: "Kick", danger: true }))) return;
    await mutate("kick", async () => {
      const r = await api.post<{ killed: number }>(`/admin/users/${id}/kick`);
      toast(`Disconnected ${r.killed} connection${r.killed === 1 ? "" : "s"}`, "info");
    }, "Kick sent");
  };

  const remove = async () => {
    if (!d) return;
    if (!(await confirm({ title: `Delete ${d.user.username}?`, body: "This permanently removes the account, its licenses and history. It can't be undone.", confirmLabel: "Delete user", danger: true }))) return;
    const ok = await mutate("delete", () => api.del(`/admin/users/${id}`), "User deleted");
    if (ok) onClose();
  };

  const entitle = (key: string, game_id: string, days: number | null, mode: "extend" | "set", success: string) =>
    mutate(key, () => api.put<AdminUserDetail>(`/admin/users/${id}/entitlements`, { game_id, days, mode }), success);

  const revoke = async (e: Entitlement) => {
    if (!(await confirm({ title: `Revoke ${e.game_name}?`, body: `${d?.user.username} will lose access immediately.`, confirmLabel: "Revoke", danger: true }))) return;
    await mutate(`revoke-${e.game_id}`, () => api.del<AdminUserDetail>(`/admin/users/${id}/entitlements/${encodeURIComponent(e.game_id)}`), "Access revoked");
  };

  return (
    <Drawer open onClose={onClose} title={d?.user.username ?? "User"}>
      {detail.error && !d ? <LoadError error={detail.error} retry={detail.reload} /> : !d ? <Loading /> : (
        <div className="space-y-5">
          <Card padded={false}>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 p-5 text-sm sm:grid-cols-3">
              <Fact label="Status">{d.user.active ? <Badge tone="ok" dot>Active</Badge> : <Badge tone="bad" dot>Disabled</Badge>}{d.user.online && <Badge tone="ok" className="ml-1">Online</Badge>}</Fact>
              <Fact label="Role">{d.user.role === "admin" ? <Badge tone="accent">Admin</Badge> : <Badge>User</Badge>}</Fact>
              <Fact label="Country">{d.user.country ?? "—"}</Fact>
              <Fact label="Email">{d.user.email ? <><span className="mr-1.5 break-all">{d.user.email}</span>{d.user.email_verified ? <Badge tone="ok">Verified</Badge> : <Badge tone="warn">Unverified</Badge>}</> : <span className="text-faint">None</span>}</Fact>
              <Fact label="Created">{fmtDate(d.user.created_at)}</Fact>
              <Fact label="Last login">{d.user.last_login ? <span title={fmtDateTime(d.user.last_login)}>{fmtRelative(d.user.last_login)}</span> : "Never"}</Fact>
              <Fact label="Last IP"><Mono>{d.user.last_ip ?? "—"}</Mono></Fact>
              <Fact label="OS">{d.user.os_version ?? "—"}</Fact>
              <Fact label="Referral code"><Mono>{d.user.referral_code ?? "—"}</Mono></Fact>
              <Fact label="Referred by">{d.user.referred_by ? <Mono>{d.user.referred_by}</Mono> : "—"}</Fact>
              <Fact label="User ID" wide><span className="flex items-center gap-1"><Mono>{d.user.id}</Mono><CopyButton text={d.user.id} label="Copy ID" /></span></Fact>
            </div>
            <div className="flex flex-wrap gap-2 border-t border-line px-5 py-3">
              {!isSelf && <Button size="sm" loading={busy === "active"} onClick={toggleActive}>{d.user.active ? "Disable" : "Enable"}</Button>}
              {!isSelf && <Button size="sm" loading={busy === "role"} onClick={toggleRole}>{d.user.role === "admin" ? "Make user" : "Make admin"}</Button>}
              <Button size="sm" icon="edit" onClick={() => setPasswordOpen(true)}>Reset password</Button>
              <Button size="sm" icon="refresh" loading={busy === "hwid"} onClick={resetHwid}>Reset HWID</Button>
              {!isSelf && <Button size="sm" loading={busy === "kick"} onClick={kick}>Kick</Button>}
              {!isSelf && <Button size="sm" variant="danger" icon="trash" loading={busy === "delete"} onClick={remove}>Delete</Button>}
            </div>
          </Card>

          <Card title="Games" padded={false}>
            {d.entitlements.length === 0 ? <p className="px-5 py-6 text-sm text-muted">No games granted.</p> : (
              <Table>
                <THead><tr><Th>Game</Th><Th>Expires</Th><Th>Source</Th><Th /></tr></THead>
                <TBody>
                  {d.entitlements.map((e) => (
                    <Tr key={e.game_id}>
                      <Td className="py-2">
                        <div className="font-medium">{e.game_name}</div>
                        {!e.active && <Badge tone="bad">Expired</Badge>}
                      </Td>
                      <Td className="whitespace-nowrap py-2 text-muted">
                        {fmtExpiry(e.expires_at)}
                        {e.expires_at && <div className="text-xs text-faint">{fmtDate(e.expires_at)}</div>}
                      </Td>
                      <Td className="py-2 text-muted">{e.source}</Td>
                      <Td className="py-2">
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button size="sm" loading={busy === `7-${e.game_id}`} onClick={() => entitle(`7-${e.game_id}`, e.game_id, 7, "extend", "Extended 7 days")}>+7d</Button>
                          <Button size="sm" loading={busy === `30-${e.game_id}`} onClick={() => entitle(`30-${e.game_id}`, e.game_id, 30, "extend", "Extended 30 days")}>+30d</Button>
                          <Button size="sm" disabled={e.expires_at === null} loading={busy === `life-${e.game_id}`} onClick={() => entitle(`life-${e.game_id}`, e.game_id, null, "set", "Set to lifetime")}>Lifetime</Button>
                          <Button size="sm" variant="danger" loading={busy === `revoke-${e.game_id}`} onClick={() => revoke(e)}>Revoke</Button>
                        </div>
                      </Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            )}
            <GrantForm games={games} busy={busy === "grant"} onGrant={(game, days, mode) => entitle("grant", game, days, mode, "Access granted")} />
          </Card>

          <Card title="Security" padded={false}>
            <div className="space-y-4 p-5">
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="text-muted">HWID</span>
                {d.user.hwid ? <span className="flex items-center gap-1"><Mono className="break-all text-fg">{d.user.hwid}</Mono><CopyButton text={d.user.hwid} label="Copy HWID" /></span> : <span className="text-faint">Not bound</span>}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Switch checked={d.user.hwid_locked} disabled={busy === "locked"} label="HWID locked"
                  onChange={(v) => patch("locked", { hwid_locked: v }, v ? "HWID locked" : "HWID unlocked")} />
                <label className="flex items-center gap-2 text-sm">
                  <span className="text-muted">Max devices</span>
                  <Select className="w-32" value={d.user.max_devices ?? ""} disabled={busy === "devices"}
                    onChange={(e) => patch("devices", { max_devices: e.target.value === "" ? null : Number(e.target.value) }, "Max devices updated")}>
                    <option value="">Default</option>
                    {Array.from({ length: 10 }, (_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                  </Select>
                </label>
              </div>
            </div>
            {d.sessions.length > 0 ? (
              <Table className="border-t border-line">
                <THead><tr><Th>Session</Th><Th>HWID</Th><Th>IP</Th><Th>Last seen</Th><Th>Expires</Th></tr></THead>
                <TBody>
                  {d.sessions.map((s) => (
                    <Tr key={s.id}>
                      <Td className="py-2">
                        <Badge tone={s.kind === "loader" ? "accent" : "neutral"}>{s.kind}</Badge>
                        {s.country && <span className="ml-2 text-xs text-muted">{s.country}</span>}
                      </Td>
                      <Td className="py-2"><Mono>{shortHash(s.hwid, 8)}</Mono></Td>
                      <Td className="py-2"><Mono>{s.ip ?? "—"}</Mono></Td>
                      <Td className="whitespace-nowrap py-2 text-muted">{fmtRelative(s.last_seen_at)}</Td>
                      <Td className="whitespace-nowrap py-2 text-muted">{fmtRelative(s.expires_at)}</Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            ) : <p className="border-t border-line px-5 py-4 text-sm text-muted">No active sessions.</p>}
          </Card>

          <Activity d={d} nameOf={nameOf} />

          <Card title="Config" padded={false}>
            <pre className="max-h-72 overflow-auto p-5 font-mono text-xs leading-relaxed text-muted">{d.config == null ? "No config." : JSON.stringify(d.config, null, 2)}</pre>
          </Card>
        </div>
      )}

      <ResetPasswordDialog open={passwordOpen} userId={id} username={d?.user.username ?? ""} onClose={() => setPasswordOpen(false)} />
    </Drawer>
  );
}

function Fact({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className={wide ? "col-span-2 sm:col-span-3" : undefined}>
      <div className="text-xs text-muted">{label}</div>
      <div className="mt-0.5 flex flex-wrap items-center">{children}</div>
    </div>
  );
}

function GrantForm({ games, busy, onGrant }: { games: Game[]; busy: boolean; onGrant: (game: string, days: number | null, mode: "extend" | "set") => Promise<boolean> }) {
  const [game, setGame] = useState("");
  const [duration, setDuration] = useState<DurationValue>(DEFAULT_DURATION);
  const [mode, setMode] = useState<"extend" | "set">("extend");
  const days = durationDays(duration);

  return (
    <div className="flex flex-wrap items-end gap-3 border-t border-line p-5">
      <Field label="Grant game" className="min-w-40 flex-1">
        <Select value={game} onChange={(e) => setGame(e.target.value)}>
          <option value="">Select a game…</option>
          {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </Select>
      </Field>
      <DurationField label="Duration" value={duration} onChange={setDuration} />
      <Field label="Mode">
        <Select value={mode} onChange={(e) => setMode(e.target.value as "extend" | "set")}>
          <option value="extend">Extend</option>
          <option value="set">Set</option>
        </Select>
      </Field>
      <Button variant="primary" loading={busy} disabled={!game || days === undefined} onClick={async () => { if (days !== undefined && await onGrant(game, days, mode)) setGame(""); }}>Grant</Button>
    </div>
  );
}

function ResetPasswordDialog({ open, userId, username, onClose }: { open: boolean; userId: string; username: string; onClose: () => void }) {
  const [password, setPassword] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const reset = useAction((body: object) => api.post<{ password: string }>(`/admin/users/${userId}/password`, body));

  const close = () => { setPassword(""); setResult(null); onClose(); };

  return (
    <Dialog open={open} onClose={close} title={`Reset password for ${username}`}
      description={result ? "This password is shown once. Copy it now." : "Their existing sessions stay signed in until they expire."}
      footer={result ? <Button variant="primary" onClick={close}>Done</Button> : <>
        <Button variant="ghost" onClick={close}>Cancel</Button>
        <Button variant="primary" loading={reset.pending} onClick={async () => {
          const r = await reset.run({ password: password || undefined });
          if (r) setResult(r.password);
        }}>Reset password</Button>
      </>}>
      {result ? <SecretBox value={result} /> : (
        <div className="space-y-3">
          <Field label="New password" hint="Leave blank to generate one"><Input value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" /></Field>
          <ErrorText>{reset.error}</ErrorText>
        </div>
      )}
    </Dialog>
  );
}

function Activity({ d, nameOf }: { d: AdminUserDetail; nameOf: (id: string) => string }) {
  const [tab, setTab] = useState<ActivityTab>("logins");
  const errorCount = d.errors.length + d.crashes.length;

  return (
    <Card padded={false} title="Activity">
      <div className="px-3">
        <Tabs<ActivityTab> value={tab} onChange={setTab} tabs={[
          { id: "logins", label: "Logins", count: d.logins.length },
          { id: "tickets", label: "Tickets", count: d.tickets.length },
          { id: "payments", label: "Payments", count: d.payments.length },
          { id: "errors", label: "Errors & crashes", count: errorCount },
          { id: "redemptions", label: "Redemptions", count: d.redemptions.length },
        ]} />
      </div>
      <div className="max-h-96 overflow-y-auto">
        {tab === "logins" && (d.logins.length === 0 ? <Empty>No logins recorded.</Empty> : (
          <Table>
            <THead><tr><Th>When</Th><Th>Result</Th><Th>Client</Th><Th>IP</Th><Th>Country</Th><Th>HWID</Th></tr></THead>
            <TBody>
              {d.logins.map((l) => (
                <Tr key={l.id}>
                  <Td className="whitespace-nowrap py-2 text-muted" ><span title={fmtDateTime(l.created_at)}>{fmtRelative(l.created_at)}</span></Td>
                  <Td className="py-2">{l.success ? <Badge tone="ok">OK</Badge> : <Badge tone="bad">Failed</Badge>}</Td>
                  <Td className="py-2 text-muted">{l.client ?? "—"}</Td>
                  <Td className="py-2"><Mono>{l.ip ?? "—"}</Mono></Td>
                  <Td className="py-2 text-muted">{l.country ?? "—"}</Td>
                  <Td className="py-2"><Mono>{shortHash(l.hwid, 8)}</Mono></Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        ))}

        {tab === "tickets" && (d.tickets.length === 0 ? <Empty>No tickets.</Empty> : (
          <ul className="divide-y divide-line">
            {d.tickets.map((t) => (
              <li key={t.id}>
                <Link to={`/admin/tickets/${t.id}`} className="flex items-center gap-3 px-5 py-3 hover:bg-hover/60">
                  <span className="min-w-0 flex-1 truncate text-sm">#{t.id} {t.subject}</span>
                  <Badge tone={TICKET_STATUS[t.status].tone}>{TICKET_STATUS[t.status].label}</Badge>
                  <span className="text-xs text-muted">{fmtRelative(t.updated_at)}</span>
                </Link>
              </li>
            ))}
          </ul>
        ))}

        {tab === "payments" && (d.payments.length === 0 ? <Empty>No payments.</Empty> : (
          <Table>
            <THead><tr><Th>Date</Th><Th>Amount</Th><Th>Description</Th></tr></THead>
            <TBody>
              {d.payments.map((p) => (
                <Tr key={p.id}>
                  <Td className="whitespace-nowrap py-2 text-muted">{fmtDate(p.created_at)}</Td>
                  <Td className="py-2 font-medium tabular-nums">{fmtMoney(p.amount_cents, p.currency)}</Td>
                  <Td className="py-2 text-muted">{p.description ?? "—"}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        ))}

        {tab === "errors" && (errorCount === 0 ? <Empty>No errors or crashes.</Empty> : (
          <ul className="divide-y divide-line">
            {d.errors.map((e) => (
              <li key={`e${e.id}`} className="px-5 py-3 text-sm">
                <div className="flex items-center gap-2"><Badge tone="warn">Error</Badge><Mono className="text-fg">{e.error_code ?? "unknown"}</Mono><span className="ml-auto text-xs text-muted">{fmtRelative(e.created_at)}</span></div>
                {e.message && <p className="mt-1 text-muted">{e.message}</p>}
              </li>
            ))}
            {d.crashes.map((c) => (
              <li key={`c${c.id}`} className="px-5 py-3 text-sm">
                <div className="flex items-center gap-2">
                  <Badge tone="bad">Crash</Badge><Mono className="text-fg">{c.error_code ?? "unknown"}</Mono>
                  {c.game_version && <span className="text-xs text-muted">v{c.game_version}</span>}
                  <span className="ml-auto text-xs text-muted">{fmtRelative(c.created_at)}</span>
                </div>
                {c.stack_trace && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-xs text-muted hover:text-fg">Stack trace</summary>
                    <pre className="mt-1 max-h-48 overflow-auto rounded-lg bg-bg p-2 font-mono text-[11px] text-muted">{c.stack_trace}</pre>
                  </details>
                )}
              </li>
            ))}
          </ul>
        ))}

        {tab === "redemptions" && (d.redemptions.length === 0 ? <Empty>No keys redeemed.</Empty> : (
          <Table>
            <THead><tr><Th>Key</Th><Th>Games</Th><Th>Duration</Th><Th>When</Th></tr></THead>
            <TBody>
              {d.redemptions.map((r) => (
                <Tr key={r.id}>
                  <Td className="py-2"><Mono className="text-fg">{r.key_hint}</Mono></Td>
                  <Td className="py-2"><div className="flex flex-wrap gap-1">{r.games.map((g) => <Badge key={g}>{nameOf(g)}</Badge>)}</div></Td>
                  <Td className="whitespace-nowrap py-2 text-muted">{fmtDuration(r.days)}</Td>
                  <Td className="whitespace-nowrap py-2 text-muted">{fmtRelative(r.redeemed_at)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        ))}
      </div>
    </Card>
  );
}

const Empty = ({ children }: { children: ReactNode }) => <p className="px-5 py-6 text-sm text-muted">{children}</p>;
